/**
 * Transaction process graph for rest 75% payments:
 *   - default-rest-payment
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
	// When a rest payment is initiated, a transaction is created with the initial request-payment transition.
	REQUEST_PAYMENT: 'transition/request-payment',

	// Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
	// Therefore we need to make another transition to Marketplace API,
	// to tell that the payment is confirmed.
	CONFIRM_PAYMENT: 'transition/confirm-payment',

	// The backend automatically expires/refunds the transaction.
	EXPIRE_PAYMENT: 'transition/expire-payment',

	// System automatically completes the transaction after 2 minutes.
	AUTO_COMPLETE: 'transition/auto-complete',

	// System automatically captures the rest payment after completion.
	AUTO_CAPTURE_PAYMENT: 'transition/auto-capture-payment',

	// Operator can cancel the transaction before or after capture.
	CANCEL_BEFORE_CAPTURE: 'transition/cancel-before-capture',
	CANCEL_AFTER_CAPTURE: 'transition/cancel-after-capture',

	// Payout Transaction
	PAYOUT_GENERATED: 'transition/payout-generated',

	// transaction synced
	TRANSACTION_SYNCED: 'transition/transaction-synced',
	TRANSACTION_EXPIRY_SYNCED: 'transition/transaction-expire-synced',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
	INITIAL: 'initial',
	PAYMENT_EXPIRED: 'payment-expired',
	ACCEPTED: 'accepted',
	COMPLETED: 'completed',
	PAYMENT_RELEASED: 'payment-released',
	SYNCED: 'synced',
	PAYMENT_CAPTURED: 'payment-captured',
	CANCEL: 'cancel',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 */
export const graph = {
	id: 'default-rest-payment/release-1',

	// This 'initial' state is a virtual starting point for new transaction
	// to allow the first transition to move it into the 'initial' state as requested.
	initial: 'pending-initiation',

	// States
	states: {
		'pending-initiation': {
			on: {
				[transitions.REQUEST_PAYMENT]: states.INITIAL,
			},
		},

		[states.INITIAL]: {
			on: {
				[transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
				[transitions.CONFIRM_PAYMENT]: states.ACCEPTED,
			},
		},

		[states.PAYMENT_EXPIRED]: { type: 'final' },

		[states.ACCEPTED]: {
			on: {
				[transitions.AUTO_COMPLETE]: states.COMPLETED,
			},
		},

		[states.COMPLETED]: {
			on: {
				[transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
				[transitions.AUTO_CAPTURE_PAYMENT]: states.PAYMENT_CAPTURED,
				[transitions.CANCEL_BEFORE_CAPTURE]: states.CANCEL,
			},
		},

		[states.PAYMENT_EXPIRED]: {
			on: {
				[transitions.TRANSACTION_EXPIRY_SYNCED]: states.SYNCED,
			}
		},

		[states.PAYMENT_CAPTURED]: {
			on: {
				[transitions.CANCEL_AFTER_CAPTURE]: states.CANCEL,
				[transitions.PAYOUT_GENERATED]: states.PAYMENT_RELEASED,
			},
		},

		[states.PAYMENT_RELEASED]: { type: 'final' },

		[states.CANCEL]: {
			on: {
				[transitions.TRANSACTION_SYNCED]: states.SYNCED,
			}
		},
	},
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
export const isRelevantPastTransition = transition => {
	return [
		transitions.REQUEST_PAYMENT,
		transitions.CONFIRM_PAYMENT,
		transitions.AUTO_COMPLETE,
		transitions.EXPIRE_PAYMENT,
		transitions.AUTO_CAPTURE_PAYMENT,
		transitions.CANCEL_BEFORE_CAPTURE,
		transitions.CANCEL_AFTER_CAPTURE,
	].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
	return [transitions.REQUEST_PAYMENT].includes(transition);
};

// Check when transaction is completed
export const isCompleted = transition => {
	return [transitions.AUTO_CAPTURE_PAYMENT].includes(transition);
};

// Check when transaction is refunded
export const isRefunded = transition => {
	return [
		transitions.EXPIRE_PAYMENT,
		transitions.CANCEL_BEFORE_CAPTURE,
		transitions.CANCEL_AFTER_CAPTURE,
	].includes(transition);
};

export const statesNeedingProviderAttention = [];
export const statesNeedingCustomerAttention = [];
